/**
 * EmptyMemoryCard
 *
 * Displayed when no AI therapist memory exists yet for a user or partner.
 * Shows a friendly explanation and an optional "Generate now" button so the
 * user doesn't have to wait for the next reflection to populate it.
 *
 * Responsibilities:
 * - Renders card with brain icon, configurable title and description
 * - Optional "Generate now" button with loading spinner
 * - canRegenerate prop hides the button entirely (e.g. partner with zero reflections)
 */

import { Brain, RefreshCw, Loader2 } from "lucide-react";
import { Card } from "../ui/card";
import { Button } from "../ui/button";

export interface EmptyMemoryCardProps {
  title: string;
  description: string;
  onRegenerate?: () => Promise<void>;
  regeneratePending?: boolean;
  /** Set to false to hide the regenerate button entirely */
  canRegenerate?: boolean;
}

export function EmptyMemoryCard({
  title,
  description,
  onRegenerate,
  regeneratePending = false,
  canRegenerate = true,
}: EmptyMemoryCardProps) {
  const showButton = canRegenerate && !!onRegenerate;

  return (
    <Card className="p-6">
      <div className="mb-4 flex items-center gap-2">
        <Brain className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold text-foreground">{title}</h3>
      </div>
      <p className="text-sm text-muted-foreground">{description}</p>
      {showButton && (
        <div className="mt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={onRegenerate}
            disabled={regeneratePending}
          >
            {regeneratePending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Generate now
          </Button>
        </div>
      )}
    </Card>
  );
}
